/* Select actual gift-card variants. Client inputs never create prices or value. */
(() => {
  if (customElements.get('gift-card-product')) return;
  class GiftCardProduct extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.select = this.querySelector('[name="id"]');
      this.form = this.select?.form;
      if (!this.form) return;
      this.options = Array.from(this.select.options).filter(option => !option.disabled);
      this.range = this.querySelector('[data-amount-range]');
      this.recipient = this.querySelector('[data-recipient-toggle]');
      this.fields = this.querySelector('[data-recipient-fields]');
      this.schedule = this.querySelector('[data-schedule-toggle]');
      this.date = this.querySelector('[data-send-on]');
      this.primary = this.querySelector('[data-primary-submit]');
      this.sticky = this.querySelector('[data-gift-sticky]');
      if (this.range) {
        this.range.disabled = this.options.length < 2;
        this.range.addEventListener('input', () => {
          this.paintRange();
          const index = Math.round(Number(this.range.value) / 1000 * (this.options.length - 1));
          this.selectAmount(this.options[index]?.value, true);
        }, {signal});
        this.range.addEventListener('keydown', event => {
          const current = this.options.findIndex(option => option.value === this.select.value);
          const next = {ArrowRight:current+1, ArrowUp:current+1, ArrowLeft:current-1, ArrowDown:current-1, PageUp:current+3, PageDown:current-3, Home:0, End:this.options.length-1}[event.key];
          if (next === undefined) return;
          event.preventDefault();
          this.selectAmount(this.options[Math.max(0,Math.min(this.options.length-1,next))]?.value);
        }, {signal});
      }
      this.select.addEventListener('change', () => this.updateAmount(), {signal});
      this.recipient?.addEventListener('change', () => this.updateRecipient(), {signal});
      this.schedule?.addEventListener('change', () => this.updateRecipient(), {signal});
      this.querySelector('[data-recipient-name]')?.addEventListener('input', () => this.updateNote(), {signal});
      this.querySelector('[data-gift-message]')?.addEventListener('input', () => this.updateNote(), {signal});
      this.querySelector('[data-sticky-submit]')?.addEventListener('click', () => {
        if (!this.form.checkValidity()) {
          this.form.querySelector(':invalid')?.scrollIntoView({block:'center', behavior:'auto'});
          this.form.reportValidity();
          return;
        }
        if (!this.primary.disabled && this.primary.getAttribute('aria-disabled') !== 'true') this.form.requestSubmit(this.primary);
      }, {signal});
      this.form.addEventListener('submit', event => {
        if (this.dataset.prototype === 'true') {
          event.preventDefault();
          this.querySelector('[data-prototype-status]').textContent = 'Local concept: ' + this.select.selectedOptions[0].textContent.trim() + ' selected. No order was placed.';
        }
      }, {signal});
      if (this.date) {
        const localDate = (date) => [date.getFullYear(), String(date.getMonth()+1).padStart(2,'0'), String(date.getDate()).padStart(2,'0')].join('-');
        const today = new Date(); const latest = new Date(); latest.setDate(latest.getDate()+90);
        this.date.min = localDate(today); this.date.max = localDate(latest);
      }
      const offset = this.querySelector('[data-gift-offset]');
      if (offset) offset.value = String(new Date().getTimezoneOffset());
      this.querySelector('.gift-card-hero')?.classList.add('gift-card-hero--enhanced');
      this.querySelectorAll('[data-enhanced-only]').forEach(element => element.hidden = false);
      this.updateAmount(); this.updateRecipient();
      if (this.sticky) {
        const syncSticky = () => {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => {
            this.sticky.hidden = this.primary.getBoundingClientRect().bottom > 0;
          });
        };
        window.addEventListener('scroll', syncSticky, {signal, passive:true});
        window.addEventListener('resize', syncSticky, {signal, passive:true});
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(syncSticky);
          this.observer.observe(this.form);
        }
        syncSticky();
      }
      this.busyObserver = new MutationObserver(() => {
        const stickyButton = this.querySelector('[data-sticky-submit]');
        if (stickyButton) stickyButton.disabled = this.primary.disabled || this.primary.getAttribute('aria-disabled') === 'true';
      });
      this.busyObserver.observe(this.primary, {attributes:true,attributeFilter:['disabled','aria-disabled']});
    }
    selectAmount(id, keepPosition = false) {
      if (!id || !this.options.some(option => option.value === id)) return;
      if (this.select.value === id) {
        if (!keepPosition) this.updateAmount();
        return;
      }
      this.keepPosition = keepPosition;
      this.select.value = id;
      this.select.dispatchEvent(new Event('change', {bubbles:true}));
      this.keepPosition = false;
    }
    paintRange() {
      this.range.style.setProperty('--gift-position', `${Number(this.range.value) / 10}%`);
    }
    updateAmount() {
      const selected = this.select.selectedOptions[0];
      if (!selected) return;
      const amount = selected.dataset.money || selected.textContent.trim();
      const faceAmount = selected.dataset.giftUsd || amount;
      this.querySelectorAll('[data-gift-face-value]').forEach(node => { node.textContent = faceAmount; node.dataset.longValue = String(faceAmount.length > 8); });
      this.querySelectorAll('[data-gift-value]').forEach(node => { node.textContent = amount; node.dataset.longValue = String(amount.replace(/\s/g,'').length > 8); });
      if (this.range) {
        if (!this.keepPosition) this.range.value = Math.max(0, this.options.findIndex(option => option.value === selected.value)) / Math.max(1,this.options.length-1) * 1000;
        this.range.setAttribute('aria-valuetext', faceAmount);
        this.paintRange();
      }
      this.primary.disabled = selected.disabled || !this.options.length;
      const label = this.primary.querySelector('[data-gift-submit-label]');
      if (label) label.textContent = this.primary.disabled ? label.dataset.unavailableLabel : label.dataset.availableLabel;
    }
    updateRecipient() {
      if (!this.fields || !this.recipient) return;
      const enabled = this.recipient.checked;
      this.fields.hidden = !enabled;
      this.recipient.setAttribute('aria-expanded', String(enabled));
      this.fields.querySelectorAll('input, textarea').forEach(input => input.disabled = !enabled);
      const email = this.querySelector('[data-recipient-email]');
      if (email) email.required = enabled;
      if (this.date) {
        const scheduled = enabled && this.schedule.checked;
        this.date.disabled = !scheduled; this.date.required = scheduled;
        this.date.closest('[data-date-field]').hidden = !scheduled;
      }
      this.updateNote();
    }
    updateNote() {
      const note = this.querySelector('[data-note-preview]');
      if (!note) return;
      const name = this.querySelector('[data-recipient-name]')?.value.trim() || '';
      const message = this.querySelector('[data-gift-message]')?.value.trim() || '';
      note.hidden = !this.recipient?.checked || (!name && !message);
      note.querySelector('strong').textContent = name ? 'For ' + name : 'A little note from you';
      note.querySelector('p').textContent = message;
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.busyObserver?.disconnect(); cancelAnimationFrame(this.scrollFrame); }
  }
  customElements.define('gift-card-product', GiftCardProduct);
})();
